import {
  AfterViewInit,
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef,
} from "@angular/core";
import { PanzoomObject } from "@panzoom/panzoom";
import { SvgIconComponent } from "angular-svg-icon";
import { Endpoint, EndpointOptions, jsPlumbInstance } from "jsplumb";
import { ConnectorOptionComponent } from "./flow/connector-option/connector-option.component";
import { FlowsService } from "./flows.service";
import { Router } from "@angular/router";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { UiService } from "../ui/ui.service";
import { DeleteConnectorOptionComponent } from "./flow/delete-connector-option/delete-connector-option.component";
import { MatDialog } from "@angular/material/dialog";
import { AreYouSureDialogComponent } from "../shared/dialogs/are-you-sure-dialog/are-you-sure-dialog.component";
import { FlowMenuComponent } from "./flow/flow-menu/flow-menu.component";
import { takeUntil } from "rxjs/operators";
import { FacilityDTO } from "../facilities/Facility";

export interface FlowNode {
  id: string;
  top?: number;
  left?: number;
  type: string;
  data: any;
  is_follow_up?: boolean;
  channelNode?: boolean;
  component?: FlowNodeController;
}

export interface SideBarItem {
  code: string;
  name: string;
  icon: string;
  selected: boolean;
  disabled: boolean;
  disabledFn?: () => boolean;
  onFlowAreaClick?: Function;
  onSidebarClick?: Function;
  channel: boolean;
}

//Obseravble operator that completes once we get our first true on our behaviour subject
export function cacheReadyOperator() {
  return (source: Observable<any>) => {
    return new Observable((subscriber) => {
      source.subscribe(
        (value) => {
          if (value === true) {
            subscriber.next(value);
            subscriber.complete();
          }
        },
        (error) => {
          subscriber.error(error);
        },
        () => {
          subscriber.complete();
        },
      );
    });
  };
}

@Component({
  selector: "flow-node-component",
  template: `ERROR: DO NOT USE THIS COMPONENT DIRECTLY, ONLY EXTEND`,
})
export abstract class FlowNodeController
  implements AfterViewInit, OnInit, OnDestroy
{
  @Input() nodes!: FlowNode[];
  @Input() connections!: any[];
  @Input() node!: FlowNode;
  @Input() jsPlumbInstance!: jsPlumbInstance;
  @Input() componentRef!: ComponentRef<FlowNodeController>;
  @Input() panzoom!: PanzoomObject;
  @Input() mode: any;
  @Input() flowMenuViewContainerRef!: ViewContainerRef;
  @Input() onMenuOpenChange!: Subject<any>;
  @Input() onNodeOrConnectorMoved!: Subject<any>;
  @Input() onMenuSizeChange!: Subject<void>;
  @Input() selectedFlowItemSubject!: BehaviorSubject<
    FlowNodeController | ConnectorOptionComponent | null | undefined
  >;
  @Input() internal!: boolean;
  @Input() facilities!: FacilityDTO[];

  @Output() onNodeReady = new EventEmitter<ComponentRef<FlowNodeController>>();
  @Output() onNodeDelete = new EventEmitter<ComponentRef<FlowNodeController>>();
  @Output() onDeleteConnector = new EventEmitter<any>();
  @Output() onStartNodeConnection = new EventEmitter<FlowNodeController>();
  @Output() onCompleteNodeConnection = new EventEmitter<FlowNodeController>();
  @Output() onNodeSelected = new EventEmitter<FlowNodeController | null>();

  @Output() onConnectorOptionsSelectChange =
    new EventEmitter<ConnectorOptionComponent | null>();
  @ViewChild("nodeElement") nodeElement!: ElementRef;
  menuComponent?: ComponentRef<FlowMenuComponent>;

  private _selected: boolean = false;
  linkOptionSelected: boolean = false;
  mousePosition = {
    x: 0,
    y: 0,
  };
  menuOpen: boolean = false;
  dragging: boolean = false;
  noTopEndpoint: boolean = false;
  touched: boolean = false;
  topEndPoint?: Endpoint;
  bottomEndPoint?: Endpoint;
  onMenuInstantiated = new Subject();
  mutationObserver!: MutationObserver;
  connectorOptionComponent!: ComponentRef<ConnectorOptionComponent>;
  onComponentDestroy = new Subject();

  constructor(
    public factoryResolver: ComponentFactoryResolver,
    public ViewContainerRef: ViewContainerRef,
    public FlowsService: FlowsService,
    public Router: Router,
    public uiService: UiService,
    public MatDialog: MatDialog,
  ) {}

  async ngOnInit() {
    //Wait for menu instantiation
    this.onMenuInstantiated
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe(() => {

        //console.log('NODE MENU READY')

        //On menu open/close
        this.menuComponent!.instance.onMenuOpenChange.pipe(
          takeUntil(this.onComponentDestroy),
        ).subscribe((menuOpenChange) => {
          this.onMenuOpenChange.next({
            type: "node",
            menuOpenChange,
            element: this.nodeElement,
            menuElement: this.menuComponent!.instance.nodeMenuElement,
          });
        });

        //On node move
        this.mutationObserver = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            //If style changes(we're assuming the node moves) then emit change
            if (mutation.attributeName === "style") {
              this.onNodeOrConnectorMoved.next({
                type: "node",
                component: this,
              });
            }
          });
        });

        this.mutationObserver.observe(this.nodeElement.nativeElement, {
          attributes: true,
          childList: true,
          characterData: true,
        });

        this.initEndpoints();

      });
  }

  ngOnDestroy() {
    //If menu component is there, destroy it on nodes destroy
    if (this.menuComponent) {
      this.menuComponent.destroy();
    }
    if (!this.onMenuInstantiated.isStopped) {
      this.onMenuInstantiated.complete();
    }

    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }

    this.onComponentDestroy.next();
    this.onComponentDestroy.complete();
  }

  ngAfterViewInit() {
    //console.log('NODE AFTER VIEW INIT')
    //this.initEndpoints();

    const { id } = this.node;

    this.jsPlumbInstance.draggable(id);
    this.jsPlumbInstance.setDraggable(id, false);
  }

  get touchedAndNotSelected() {
    return this.touched && !this.selected;
  }

  initEndpoints() {
    //console.log('INIT ENDPOINTS')
    const dropOptions = {
      tolerance: "touch",
      hoverClass: "dropHover",
      activeClass: "dragActive",
    };
    let Endpoint1: EndpointOptions = {
      endpoint: ["Blank", {}],
      isSource: true,
      scope: "jsPlumb_DefaultScope",
      //@ts-ignore
      connectorStyle: { stroke: "#707070", strokeWidth: 2, dashstyle: "4 3" },
      maxConnections: 30,
      isTarget: false,
      connectorOverlays: [
        [
          "Custom",
          {
            create: (connection: any) => {
              const factory =
                this.factoryResolver.resolveComponentFactory(SvgIconComponent);
              const svgIconComponent = factory.create(
                this.ViewContainerRef.injector,
              );
              svgIconComponent.instance.src = "assets/svg/flows/flow-arrow.svg";

              this.ViewContainerRef.insert(svgIconComponent.hostView);

              //@ts-ignore
              return svgIconComponent.instance.element.nativeElement;
            },
            location: 0.985,
            id: "connectorArrow",
          },
        ],
        [
          "Custom",
          {
            create: (connection: any) => {
              const factory = this.factoryResolver.resolveComponentFactory(
                ConnectorOptionComponent,
              );

              const data = connection.getParameters();

              const connectorOptionComponent = factory.create(
                this.ViewContainerRef.injector,
              );

              const connectionRef = this.connections.find((con) => {
                return con.id === data.id;
              });

              connectionRef.component = connectorOptionComponent.instance;

              connectorOptionComponent.instance.data = data;
              connectorOptionComponent.instance.mode = this.mode;
              connectorOptionComponent.instance.flowMenuViewContainerRef = this.flowMenuViewContainerRef;
              connectorOptionComponent.instance.internal = this.internal;
              connectorOptionComponent.instance.onOptionsSelectChange
                .pipe(takeUntil(this.onComponentDestroy))
                .subscribe(
                  (connectOptionComponent: ConnectorOptionComponent | null) => {
                    this.onConnectorOptionsSelectChange.emit(
                      connectOptionComponent,
                    );
                  },
                );
              connectorOptionComponent.instance.onMenuOpenChange =
                this.onMenuOpenChange;

              connectorOptionComponent.instance.onNodeOrConnectorMoved =
                this.onNodeOrConnectorMoved;

              connectorOptionComponent.instance.onMenuSizeChange =
                this.onMenuSizeChange;

              this.ViewContainerRef.insert(connectorOptionComponent.hostView);

              this.connectorOptionComponent = connectorOptionComponent;

              return connectorOptionComponent.instance.element.nativeElement;
            },
            location: 0.35,
            id: "connectorOptions",
          },
        ],
        [
          "Custom",
          {
            create: (connection: any) => {
              const factory = this.factoryResolver.resolveComponentFactory(
                DeleteConnectorOptionComponent,
              );

              const data = connection.getParameters();

              const connectorDeleteComponent = factory.create(
                this.ViewContainerRef.injector,
              );

              connectorDeleteComponent.instance.data = data;
              connectorDeleteComponent.instance.connectorOptionComponent =
                this.connectorOptionComponent;
              connectorDeleteComponent.instance.mode = this.mode;
              connectorDeleteComponent.instance.onDeleteConnector
                .pipe(takeUntil(this.onComponentDestroy))
                .subscribe((data: any) => {
                  this.onConnectorDeleted(data);
                });
              this.ViewContainerRef.insert(connectorDeleteComponent.hostView);

              return connectorDeleteComponent.instance.element.nativeElement;
            },
            location: 0.78,
            id: "connectorDelete",
          },
        ],
      ],
      dropOptions,
    };
    let Endpoint2: EndpointOptions = {
      endpoint: ["Blank", {}],
      isSource: false,
      scope: "jsPlumb_DefaultScope",
      maxConnections: 1,
      isTarget: true,
      dropOptions,
    };
    const { id } = this.node;
    this.bottomEndPoint = this.jsPlumbInstance.addEndpoint(
      id,
      //@ts-ignore
      { anchor: "Bottom", uuid: id + "_bottom" },
      Endpoint1,
    ) as Endpoint;
    this.bottomEndPoint.maxConnections = Endpoint1.maxConnections;
    if (!this.noTopEndpoint) {
      this.topEndPoint = this.jsPlumbInstance.addEndpoint(
        id,
        //@ts-ignore
        { anchor: "Top", uuid: id + "_top" },
        Endpoint2,
      ) as Endpoint;
      this.topEndPoint.maxConnections = Endpoint2.maxConnections;
    }
  }

  onNodeClick($event: MouseEvent) {
    //Disable panzoom panning
    $event.stopPropagation();

    if (this.mode.inNodeConnectionMode) {
      if (this.canBeConnectedTo) {
        this.onCompleteNodeConnection.emit(this);
        // this.menuTrigger.closeMenu();
      }
    } else {
      //on drag dont open menu
      if (
        !(
          this.mousePosition.x === $event.screenX &&
          this.mousePosition.y === $event.screenY
        )
      ) {
        // this.menuTrigger.closeMenu();
      } else {
        this.toggleNode();
      }
    }
  }

  toggleNode() {
    if (this.selected) {
      //If already selected, close
      this.onNodeSelected.emit(null);
    } else {
      //menu opened
      this.onNodeSelected.emit(this);
      this.touched = true;
    }
  }

  onMouseDown($event: MouseEvent) {
    this.mousePosition.x = $event.screenX;
    this.mousePosition.y = $event.screenY;
    this.dragging = true;
  }

  onMouseUp() {
    this.dragging = false;

    this.node.left = this.nodeElement.nativeElement.offsetLeft;
    this.node.top = this.nodeElement.nativeElement.offsetTop;
  }

  deleteNode() {
    const connections = this.jsPlumbInstance.getAllConnections();
    const nodeConnections = connections.filter((connection) => {
      return (
        connection.sourceId === this.node.id ||
        connection.targetId === this.node.id
      );
    });

    //remove connections from array
    const connectionsToRemove = this.connections.filter((connection) => {
      return nodeConnections.find((con) => con.id === connection.id);
    });
    for (let jsPlumbConnection of connectionsToRemove) {
      const con = this.connections.find(
        (con) => con.id === jsPlumbConnection.id,
      );
      const conIndex = this.nodes.indexOf(con);
      this.connections.splice(conIndex, 1);
    }

    //delete all associated connections
    for (let connect of nodeConnections) {
      this.jsPlumbInstance.deleteConnection(connect);
    }

    //delete all endpoints
    const endpoints = this.jsPlumbInstance.getEndpoints(this.node.id);
    for (let endpoint of endpoints) {
      this.jsPlumbInstance.deleteEndpoint(endpoint);
    }

    //remove from nodes
    const nodeIndex = this.nodes.indexOf(this.node);
    this.nodes.splice(nodeIndex, 1);

    this.onNodeDelete.emit(this.componentRef);
  }

  onConnectorDeleted(data: any) {
    this.onDeleteConnector.emit(data);
  }

  onStartLinkOptionSelected($event: Event) {
    $event.stopPropagation();
    if (!this.linkOptionSelected) {
      this.linkOptionSelected = true;
      this.onStartNodeConnection.emit(this);
    }
  }

  onDeleteNodeSelected($event: Event) {
    $event.stopPropagation();
    this.MatDialog.open(AreYouSureDialogComponent, {
      data: {
        areYouSureInfo: {
          description: `You are about to delete this node and its connections.`,
          warning: "Warning: this is not reversible!",
        },
      },
    })
      .afterClosed()
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((res) => {
        if (res) {
          this.deleteNode();
        }
      });
  }

  get invalid() {
    if (this.menuComponent) {
      return this.menuComponent.instance.invalid;
    }
    return false;
  }

  get canBeConnectedTo() {
    let canBeConnected = false;

    if (
      this.topEndPoint &&
      //@ts-ignore
      this.topEndPoint.isTarget &&
      !this.linkOptionSelected
    ) {
      canBeConnected = this.topEndPoint.connections
        ? this.topEndPoint.connections?.length < this.topEndPoint.maxConnections
        : this.topEndPoint.maxConnections > 0;
    }

    if (this.node.is_follow_up && canBeConnected) {
      if (
        this.selectedFlowItemSubject.value &&
        this.selectedFlowItemSubject.value instanceof FlowNodeController
      ) {
        canBeConnected = !!this.selectedFlowItemSubject.value.node.channelNode;
      }
    }

    return canBeConnected;
  }

  get selected() {
    return this._selected;
  }

  setSelected(selected: boolean) {
    this._selected = selected;
    if (this.menuComponent) {
      this.menuComponent.instance.selected = selected;
    }
  }

  get nodeDTO() {
    return this.menuComponent?.instance.nodeDTO;
  }
}
